import { useMemo } from 'react'
import { type TimeRangeKey } from '../shared/insights'
import { INSIGHTS_CACHE_TTL_SECONDS } from '../shared/insights/utils/insightsUtils'
import { buildEmptyData, getDateRange, transformInsightsApiResponse } from '../shared/insights/utils/insightsTransforms'
import { useGetAdvisorInsightsDataQuery, useGetInsightsDataQuery } from './insightsDataApi'

interface UseInsightsDataOptions {
  enabled?: boolean
  userIdList?: string[]
}

export const useInsightsData = (accountId: string, timeRange: TimeRangeKey, { enabled = true, userIdList }: UseInsightsDataOptions = {}) => {
  const {
    data: apiData,
    isFetching,
    isLoading,
    isError,
  } = useGetInsightsDataQuery(
    { accountId, ...getDateRange(timeRange), userIdList: userIdList?.length ? userIdList : undefined },
    { skip: !enabled || !accountId, refetchOnMountOrArgChange: INSIGHTS_CACHE_TTL_SECONDS },
  )

  const data = useMemo(() => transformInsightsApiResponse(apiData, timeRange), [apiData, timeRange])

  const displayData = data ?? (enabled && !isError ? buildEmptyData(timeRange) : null)

  return {
    data: isLoading ? null : displayData,
    isLoading,
    isFetching,
    isError,
  }
}

interface UseAdvisorInsightsDataOptions {
  enabled?: boolean
}

export const ADVISOR_INSIGHTS_TIME_RANGE: TimeRangeKey = '30D'

export const useAdvisorInsightsData = (accountId: string, meetingTemplateId: string, { enabled = true }: UseAdvisorInsightsDataOptions = {}) => {
  const {
    data: apiData,
    isFetching,
    isLoading,
    isError,
  } = useGetAdvisorInsightsDataQuery(
    { accountId, ...getDateRange(ADVISOR_INSIGHTS_TIME_RANGE), meetingTemplateId },
    { skip: !enabled, refetchOnMountOrArgChange: true },
  )

  const data = useMemo(() => transformInsightsApiResponse(apiData, ADVISOR_INSIGHTS_TIME_RANGE), [apiData])

  const displayData = data ?? (enabled && !isError ? buildEmptyData(ADVISOR_INSIGHTS_TIME_RANGE) : null)

  return {
    data: isLoading ? null : displayData,
    isLoading,
    isFetching,
    isError,
  }
}
